import { Container } from '@/atoms/Container/Container';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/atoms/Dialog/Dialog';
import { Link } from '@/atoms/Link/Link';
import { List } from '@/atoms/List/List';
import { Typography } from '@/atoms/Typography/Typography';

const EMAIL_PRIVACY_URL = 'mailto:privacy@synonym.to';

interface DialogPrivacyProps {
  trigger?: React.ReactNode;
}

export function DialogPrivacy({ trigger }: DialogPrivacyProps) {
  const baseCSS = 'text-muted-foreground text-base font-normal';

  const defaultTrigger = (
    <Typography as="span" size="sm" className="cursor-pointer font-medium text-brand">
      Privacy Policy
    </Typography>
  );

  return (
    <Dialog>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="sm:max-w-xl" hiddenTitle="Privacy Policy">
        <DialogHeader className="pr-6">
          <DialogTitle>Privacy Policy</DialogTitle>
        </DialogHeader>
        <Container className="h-[320px] overflow-y-auto pr-4">
          <Container className="flex-col gap-4">
            {/** Section 1 */}
            <Typography className={baseCSS}>Effective Date: January 29, 2026</Typography>
            <Typography className={baseCSS}>
              SCOPE
              <br /> This Privacy Policy (“Policy“) describes how Synonym Software Ltd. treats personal information on
              the Pubky progressive web application and the products, services and features made available as part of
              the application (collectively, the “Platform“).
            </Typography>
            <Typography className={baseCSS}>
              POLICY SUMMARY
              <br /> This summary offers a concise overview of how Synonym Software Ltd. (“Synonym“) collects, uses,
              shares, and protects your personal information on the Pubky platform. For full details, please read the
              complete Privacy Policy.
            </Typography>

            {/** Section 2 */}
            <Typography className={baseCSS}>
              NOTICE AT COLLECTION
              <br />
              We collect, and in the past 12 months have collected, the following categories of information from and
              about you: identifiers (such as your public key, IP address, account identifiers and cryptographic keys),
              internet or similar network activity (such as device information, log data, and usage information) and
              communications (where you contact us directly). We collect these categories of information from you in
              order to provide our services to you, to run our business, to communicate with you, and as required by
              law. We may have disclosed each of these categories of personal information for a business purpose as
              described below. We have not “sold” or “shared” personal information in the past 12 months, as those terms
              are defined by the California Consumer Privacy Act (“CCPA”) or General Data Protection Regulation
              (“GDPR”). For a description of your rights under the CCPA or GDPR, and instructions on how to exercise
              your rights, please see the Your Privacy Rights section below.
            </Typography>
            <Typography className={baseCSS}>
              We will retain each of these categories of personal information for as long as it is necessary for the
              purposes for which it was collected. Retention periods are determined based on: (i) duration of the user
              relationship; (ii) legal and regulatory requirements; (iii) security and fraud-prevention needs; and (iv)
              dispute resolution obligations. When data is no longer required, it is securely deleted or anonymized.
            </Typography>

            {/** Section 3 */}
            <Typography className={baseCSS}>TYPES OF INFORMATION WE COLLECT</Typography>
            <Typography className={baseCSS}>
              We collect information from you and about you. Here are some examples of the information we may collect:
            </Typography>
            <List>
              <li>
                <strong>Identifiers.</strong> This includes your Pubky address and cryptographic keys.
              </li>
              <li>
                <strong>Phone Number.</strong> We will collect your phone number you provide for SMS verification
                purposes.
              </li>
              <li>
                <strong>Information You Submit.</strong> We may collect any information that you submit or provide to us
                if you contact us.
              </li>
              <li>
                <strong>Device Information.</strong> For example, we may collect information about the type of device
                you use to access our Platform. We may also collect your device identifier, IP address or mobile
                operating system. Based on your IP address, we may also collect information relating to your general
                location.
              </li>
              <li>
                <strong>Payment Information.</strong> If you choose the Lightning payment verification, we collect
                payment hashes and transaction timestamps associated with the Lightning payment.
              </li>
              <li>
                <strong>Profile Information.</strong> We may collect information you provide when creating your profile,
                such as your display name, bio, profile picture, and links you choose to share.
              </li>
              <li>
                <strong>Content You Post.</strong> This includes posts, replies, images, videos, and other content you
                submit to the Platform.
              </li>
              <li>
                <strong>Social Connections.</strong> This includes information about users you follow, users who follow
                you, and users you have muted.
              </li>
              <li>
                <strong>Bookmarks.</strong> This includes posts you have bookmarked for later viewing.
              </li>
            </List>

            {/** Section 4 */}
            <Typography className={baseCSS}>LEGAL BASIS</Typography>
            <Typography className={baseCSS}>
              We must have a lawful basis (a valid legal reason) for using your information, and more specifically
              personal data. Our legal basis will be one of the following:
            </Typography>
            <List>
              <li>
                <strong>Consent:</strong> You have agreed to us collecting your personal data, or sensitive personal
                data, for example when you tick a box to indicate consent.
              </li>
              <li>
                <strong>Keeping to our contracts and agreements with you:</strong> We need certain personal data to
                provide our services and cannot provide them without this personal data.
              </li>
              <li>
                <strong>Legal obligations:</strong> At times, we have a legal responsibility to collect and store your
                personal data (for example, under anti-money laundering laws we must hold certain information about our
                users).
              </li>
              <li>
                <strong>Legitimate interests:</strong> We sometimes collect and use your personal data because we have a
                legitimate reason to use it and this is reasonable when balanced against your human rights and freedoms.
                Where we rely on legitimate interests, such interests include operating, securing, and improving the
                Platform and preventing fraud.
              </li>
              <li>
                <strong>Substantial public interest:</strong> Where we process your personal data, or your sensitive
                personal data (sometimes known as special category personal data under GDPR), to adhere to government
                regulations or guidance, such as our obligation to prevent fraud or support you if you are, or become, a
                vulnerable user.
              </li>
            </List>

            {/** Section 5 */}
            <Typography className={baseCSS}>HOW WE COLLECT YOUR INFORMATION</Typography>
            <Typography className={baseCSS}>
              We collect your information in different ways. Below are some examples of how we may collect your
              information.
            </Typography>
            <List>
              <li>
                <strong>Directly From You.</strong> For example, when you create a profile, post on the Platform, or
                submit an inquiry to us.
              </li>
              <li>
                <strong>Passively.</strong> For example, when you visit and navigate our Platform on any device or scan
                a provided QR code. We may also collect information about users over time and across different websites
                and devices when you use the Platform.
              </li>
              <li>
                <strong>From Third Parties.</strong> We may receive information about you from other sources. For
                example, this may include information we receive from our SMS verification service provider during the
                account registration process.
              </li>
              <li>
                <strong>By Combining Information.</strong> We may combine information that we collect offline with
                information we collect through our Platform. We may also combine information we collect about you from
                the different devices you use to access our Platform.
              </li>
              <li>
                <strong>Privacy-Focused Analytics, to help operate, secure, and improve the Platform.</strong> We may
                use privacy-focused analytics service providers to measure aggregate usage of the Platform, such as page
                views, referral sources, device types, and general usage trends. Such services operate without
                advertising cookies or cross-site tracking.
              </li>
            </List>

            {/** Section 6 */}
            <Typography className={baseCSS}>HOW WE USE YOUR INFORMATION</Typography>
            <Typography className={baseCSS}>Examples of how we may use your information include:</Typography>
            <List>
              <li>
                <strong>To Run and Improve Our Platform and Business.</strong> We may use your information to operate
                and improve the Platform. We may also use your information to provide you with information about our
                business.
              </li>
              <li>
                <strong>To Respond to Your Requests or Questions.</strong> This may include responding to your feedback.
              </li>
              <li>
                <strong>For Security and Fraud Prevention Purposes.</strong> This could include protecting our company
                and consumers who use our products and services. It may also include protecting our Platform.
              </li>
              <li>
                <strong>As Otherwise Permitted By Law or As We May Notify You.</strong> This includes using your data as
                required to comply with applicable regulations or fraud detection.
              </li>
              <li>
                <strong>As Requested or Directed By You.</strong>
              </li>
            </List>

            {/** Section 7 */}
            <Typography className={baseCSS}>HUMAN VERIFICATION</Typography>
            <Typography className={baseCSS}>
              To prevent spam and verify you are not a robot, we offer the following verification options:
            </Typography>
            <Typography className={baseCSS}>
              <strong>SMS Verification.</strong> We use a third-party SMS verification service to send one-time codes to
              your phone number. During this process, we collect:
            </Typography>
            <List>
              <li>Your phone number (stored in hashed form)</li>
              <li>Your IP address</li>
              <li>Your device user-agent</li>
              <li>Verification attempts at timestamps</li>
            </List>
            <Typography className={baseCSS}>
              Phone number verifications are rate-limited to prevent abuse. SMS verification may not be available in all
              regions.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Lightning Payment Verification.</strong> As an alternative, you may verify that you are not a
              robot by making a small Lightning Network payment. During this process, we collect:
            </Typography>
            <List>
              <li>Payment hash (invoice identifier)</li>
              <li>Payment amount</li>
              <li>Payment timestamp</li>
            </List>
            <Typography className={baseCSS}>
              Lightning payments are processed through our own infrastructure. Lightning verification may not be
              available in all regions due to applicable laws and regulatory requirements.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Invite Code Verification.</strong> You may also verify your account using an invite code provided
              by another user or by Synonym.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Local Storage.</strong> The Pubky App stores data locally on your device using browser storage
              technologies (IndexedDB) to provide a fast, offline-capable experience. Locally stored data includes:
            </Typography>
            <List>
              <li>Your profile information and posts</li>
              <li>Your social connections (following, followers, friends)</li>
              <li>Cached content from users you interact with</li>
              <li>Notification history</li>
              <li>Bookmarked posts</li>
              <li>Media file metadata</li>
            </List>
            <Typography className={baseCSS}>
              This data remains on your device and syncs with our servers when you&apos;re online. You can clear this
              data through your browser settings.
            </Typography>

            {/** Section 8 */}
            <Typography className={baseCSS}>HOW WE SHARE YOUR INFORMATION</Typography>
            <Typography className={baseCSS}>We may share your information in the following ways:</Typography>
            <List>
              <li>
                <strong>Internally.</strong> We may share your information with our parent, subsidiary, and affiliate
                entities.
              </li>
              <li>
                <strong>With Our Service Providers.</strong> We may share your information with third parties who
                perform services on our behalf. For example, this may include companies that send emails on our behalf,
                help us run our Platform, or provide security and identity verification services.
              </li>
              <li>
                <strong>On the Platform.</strong> If you choose to make posts on the Platform, anything that you post
                will be posted publicly on the Platform and visible to other users. In addition, due to the
                decentralized nature of the Platform, any information that you upload to the Platform may be hosted on
                servers hosted by other users of the Platform.
              </li>
              <li>
                <strong>With Any Successors to All or Part of Our Business.</strong> For example, if we merge with, are
                acquired by, or sell part of our business to another entity. This may include an asset sale, corporate
                reorganization or other change of control.
              </li>
              <li>
                <strong>To Comply With the Law or To Protect Ourselves.</strong> For example, this could include
                responding to a court order or subpoena. It could also include sharing information if a government
                agency or investigatory body requests. We might share information when we are investigating a potential
                fraud.
              </li>
              <li>
                <strong>For Other Reasons We May Describe to You.</strong>
              </li>
              <li>
                <strong>As Requested or Directed By You.</strong>
              </li>
            </List>

            {/** Section 9 */}
            <Typography className={baseCSS}>CHILDREN UNDER 18</Typography>
            <Typography className={baseCSS}>
              The Platform where this Policy is located is meant for adults. We do not knowingly collect personally
              identifiable data from persons under the age of 18, and strive to comply with the provisions of COPPA (The
              Children’s Online Privacy Protection Act). If you are a parent or legal guardian and think your child
              under 18 has provided us with information, please contact us at{' '}
              <Link href={EMAIL_PRIVACY_URL}>privacy@synonym.to</Link>. You can also write to us at the address listed
              at the end of this website Policy. Please mark your inquiries “COPPA Information Request.” Parents, you
              can learn more about how to protect children’s privacy on-line{' '}
              <Link
                target="_blank"
                href="https://www.consumer.ftc.gov/articles/0031-protecting-your-childs-privacy-online"
              >
                here
              </Link>
              .
            </Typography>

            {/** Section 10 */}
            <Typography className={baseCSS}>CHOICES REGARDING YOUR INFORMATION</Typography>
            <Typography className={baseCSS}>
              You have certain choices about how we use your information. Certain choices you make are browser and
              device specific.
            </Typography>
            <Typography className={baseCSS}>SMS Messages:</Typography>
            <Typography className={baseCSS}>
              We may send you SMS messages in order to verify your identity as part of the account creation process.
              Note that message and data rates may apply. The frequency of messages may vary. You may reply “HELP” if
              you need assistance or “STOP” to stop receiving SMS messages. If you opt out, we may send you a
              confirmation SMS message to acknowledge your request. We do not sell or share phone numbers to third
              parties for their own marketing purposes.
            </Typography>
            <Typography className={baseCSS}>Cookies & Other Tracking Technologies:</Typography>
            <List>
              <li>
                <strong>Cookies:</strong> Non-essential cookies are used only where permitted by applicable law and,
                where required, with your consent. You may withdraw consent at any time. For more information on how to
                control browser cookies,{' '}
                <Link
                  target="_blank"
                  href="https://www.networkadvertising.org/understanding-online-advertising/what-are-my-options"
                >
                  click here
                </Link>
                . Note that deleting or blocking cookies may impact your experience on our website, as some features may
                not be available. Certain options you select are browser and device specific.
              </li>
            </List>
            <Typography className={baseCSS}>Our Do Not Track Policy:</Typography>
            <List>
              <li>
                <strong>
                  Some browsers have “do not track” features that allow you to tell a website not to track you. These
                  features are not all uniform. We do not currently respond to those signals.
                </strong>{' '}
                If you block cookies, certain features on our sites may not work. If you block or reject cookies, not
                all of the tracking described here will stop.
              </li>
              <li>Options you select are browser and device specific.</li>
            </List>

            {/** Section 11 */}
            <Typography className={baseCSS}>YOUR PRIVACY RIGHTS</Typography>
            <Typography className={baseCSS}>
              Based on your state or country of residence, you may have the rights listed below with respect to the
              personal information that we maintain about you. We may take steps to verify your identity, as permitted
              or required under applicable law, before we process your request. Verification may include asking you to
              provide information about yourself that we can match against information already in our possession.
            </Typography>
            <List>
              <li>
                <strong>Notice.</strong> This means that you can request that we disclose what personal information we
                have collected about you, including the categories of personal information, the categories of sources
                from which the personal information is collected, the business or commercial purpose for collecting,
                selling, or sharing personal information, the categories of third parties to whom we have disclosed
                personal information, and the specific pieces of personal information we have collected about you.
              </li>
              <li>
                <strong>Access.</strong> This means that you have the right to access your personal data. This enables
                you to receive a copy of the personal data we hold about you and to check that we are lawfully
                processing it.
              </li>
              <li>
                <strong>Deletion.</strong> This means that you can request that we delete personal information about you
                which we have collected from you. Note, however, that we may not always be able to comply with your
                request of erasure for specific legal reasons which will be notified to you, if applicable, at the time
                of your request.
              </li>
              <li>
                <strong>Correction.</strong> This means that you can rectify inaccurate personal information that we
                maintain about you.
              </li>
              <li>
                <strong>Erasure.</strong> This means that you have the right to be forgotten.
              </li>
              <li>
                <strong>Object to Specific Processing.</strong> This means that you may object to processing of your
                personal information where we are relying on a legitimate interest (or those of a third party) and there
                is something about your particular situation which makes you want to object to processing on this ground
                as you feel it impacts on your fundamental rights and freedoms. In some cases, we may demonstrate that
                we have compelling legitimate grounds to process your information which override your rights and
                freedoms.
              </li>
              <li>
                <strong>Restrict Processing.</strong> This means that you may restrict processing of your personal
                information.
              </li>
              <li>
                <strong>Withdraw Consent.</strong> Where we are relying on consent to process your personal information,
                you may withdraw that consent at any time. However, this will not affect the lawfulness of any
                processing carried out before you withdraw your consent. If you withdraw your consent, we may not be
                able to provide certain products or services to you. We will advise you if this is the case at the time
                you withdraw your consent.
              </li>
              <li>
                <strong>Limit the Use or Disclosure of Sensitive Personal Information.</strong> The CCPA provides a
                right for consumers to request that business limit the use or disclosure of their “sensitive” personal
                information. In our normal business operations, we do not process sensitive personal information as that
                term is defined under CCPA. Please do not submit this information to us.
              </li>
              <li>
                <strong>Opt-Out of the Sale or Sharing of Personal Information.</strong> You may have the right to opt
                out of the “sale” or sharing of personal information with third parties for cross-contextual marketing
                purposes.{' '}
                <strong>
                  We do not knowingly “sell” or “share” any personal information, including any personal information
                  relating to consumers under the age of 16, as we understand those terms to be defined under applicable
                  law.
                </strong>
              </li>
              <li>
                <strong>Complaint.</strong> You have a right to lodge a complaint with a relevant supervisory authority.
              </li>
            </List>
            <Typography className={baseCSS}>
              Agents that you have authorized to act on your behalf may also submit requests as instructed below. The
              agent must also provide evidence that they have your written permission to submit a request on your
              behalf. If we are unable to verify the authenticity of a request, we may ask you for more information or
              may deny the request.
            </Typography>
            <Typography className={baseCSS}>
              Synonym or third-party providers may use machine learning models or automated decision-making technology
              (“ADMT”) to perform fraud detection and geoblocking, which might restrict data subjects’ access to the
              products, services, and features available through the Pubky platform. Data subjects have the right to
              obtain human intervention to express their point of view and contest such ADMT.
            </Typography>
            <Typography className={baseCSS}>
              Individuals who wish to exercise their rights as provided in this section, including submitting a request
              to opt-out or to access information about our use of ADMT, can contact us at{' '}
              <Link href={EMAIL_PRIVACY_URL}>privacy@synonym.to</Link>. Please include your name, public key, and email
              address, and indicate you are making a “Privacy Rights” request. If we deny your rights request and you
              would like to appeal, you may contact us again at
              <Link href={EMAIL_PRIVACY_URL}> privacy@synonym.to</Link>. In addition, you may delete your account or
              data associated with your account at any time by visiting the ‘Settings’ dashboard within the Pubky
              application.
            </Typography>
            <Typography className={baseCSS}>
              Should you wish to report a complaint or if you feel that we have not addressed your concern in a
              satisfactory manner, you may contact the supervisory authority of your state or country.
            </Typography>

            {/** Section 12 */}
            <Typography className={baseCSS}>SECURITY</Typography>
            <Typography className={baseCSS}>
              The Internet is not 100% secure. We cannot promise that your use of our Platform will be completely safe.
              We encourage you to use caution when using the Internet. While we use reasonable means to safeguard
              personal information under our control, due to the decentralized nature of the Platform, any information
              that you upload to the Platform may be shared in different locations and different servers. This may
              include servers not controlled or maintained by Synonym.
            </Typography>

            {/** Section 13 */}
            <Typography className={baseCSS}>STORAGE AND TRANSFER OF INFORMATION</Typography>
            <Typography className={baseCSS}>
              Due to the decentralized nature of the Platform, information we maintain may be stored or hosted in any
              jurisdiction, including the United States. If you live outside of the United States, you understand and
              agree that we may transfer your personal information to the United States. This Platform is subject to the
              laws of the United States, which may not provide the same level of protections as those in your own
              country. Personal information processed and stored in another country, including the United States, may be
              subject to disclosure or access requests by the governments, courts or law enforcement or regulatory
              agencies in that country according to its laws.
            </Typography>
            <Typography className={baseCSS}>
              Where personal data is transferred outside the European Economic Area, such transfers are carried out on
              the basis of appropriate safeguards under Article 44-49 GDPR, including Standard Contractual Clauses via
              third-party service providers.
            </Typography>

            {/** Section 14 */}
            <Typography className={baseCSS}>LINKS</Typography>
            <Typography className={baseCSS}>
              Our Platform may contain links to other third-party sites or platform that are not governed by this
              Policy. If you click on a link to a third-party site, you will be taken to a site we do not control. We
              are not responsible for the privacy practices used by third-party sites. We suggest that you read the
              privacy policies of those sites carefully.
            </Typography>
            <Typography className={baseCSS}>
              When you view posts containing links to video or social media platforms, your browser may load embedded
              content directly from those services (e.g., YouTube, Twitter/X, Vimeo). Those services may collect
              information according to their own privacy policies.
            </Typography>

            {/** Section 15 */}
            <Typography className={baseCSS}>HOW TO CONTACT US</Typography>
            <Typography className={baseCSS}>
              If you have any questions, comments or concerns with respect to our privacy practices or this Policy, or
              wish to update your information, please feel free to contact us at{' '}
              <Link href={EMAIL_PRIVACY_URL}>privacy@synonym.to</Link>.
            </Typography>

            {/** Section 16 */}
            <Typography className={baseCSS}>CHANGES IN POLICY</Typography>
            <Typography className={baseCSS}>
              From time to time, we may change our Policy. We will notify you of any material changes to our Policy as
              required by law. We will also post an updated copy on our Platform. Please check our Platform periodically
              for updates.
            </Typography>
          </Container>
        </Container>
      </DialogContent>
    </Dialog>
  );
}
